import { motion } from "framer-motion";
import { ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import NetworkCanvas from "./NetworkCanvas";
import { COMPANY } from "../lib/site";

const easeOut = [0.22, 1, 0.36, 1];

const lineWrap = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const line = {
    hidden: { y: "110%" },
    show: { y: "0%", transition: { duration: 0.8, ease: easeOut } },
};

const HEADLINE = ["Technology Solutions", "Built for", "Your Business"];

const CHIPS = [
    "Mobile Platforms",
    "Software & Systems",
    "Networks & Hardware",
    "CCTV Security",
    "Starlink Setup",
];

export default function HeroSection() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="grain relative overflow-hidden bg-gradient-to-br from-[#090d16] via-[#0f172a] to-[#12306b] pt-28 pb-20 sm:pt-32 lg:pt-40 lg:pb-28"
        >
            <NetworkCanvas />
            <div
                className="pointer-events-none absolute -right-40 -top-32 h-[520px] w-[520px] rounded-full opacity-40 blur-3xl"
                style={{
                    background:
                        "radial-gradient(circle, rgba(56,189,248,0.35), transparent 65%)",
                }}
                aria-hidden="true"
            />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:px-12">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: easeOut }}
                        className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 backdrop-blur"
                        data-testid="hero-badge-location"
                    >
                        <MapPin className="h-3.5 w-3.5 text-sky-300" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-sky-100/90">
                            {COMPANY.locality}
                        </span>
                    </motion.div>

                    <motion.h1
                        variants={lineWrap}
                        initial="hidden"
                        animate="show"
                        data-testid="hero-headline"
                        className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
                    >
                        {HEADLINE.map((text, i) => (
                            <span
                                key={i}
                                className="block overflow-hidden py-[0.05em]"
                            >
                                <motion.span
                                    variants={line}
                                    className="block"
                                >
                                    {i === 2 ? (
                                        <span className="text-gradient">
                                            {text}
                                        </span>
                                    ) : (
                                        text
                                    )}
                                </motion.span>
                            </span>
                        ))}
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: easeOut, delay: 0.55 }}
                        data-testid="hero-subhead"
                        className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
                    >
                        Empowering businesses and organizations with mobile
                        platforms, software solutions, network systems, and
                        reliable technology installation services.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: easeOut, delay: 0.7 }}
                        className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
                    >
                        <a
                            href="#contact"
                            data-testid="hero-cta-consult"
                            className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white shadow-[0_14px_34px_rgba(2,132,199,0.45)] transition-transform duration-200 hover:-translate-y-0.5"
                        >
                            Get in Touch
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </a>
                        <a
                            href="#services"
                            data-testid="hero-cta-explore"
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/10"
                        >
                            Explore Our Services
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-10 flex flex-wrap gap-2"
                    >
                        {CHIPS.map((c) => (
                            <span
                                key={c}
                                className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-300"
                            >
                                {c}
                            </span>
                        ))}
                    </motion.div>
                </div>

                {/* Visual card */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.94, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: easeOut, delay: 0.35 }}
                    className="relative hidden lg:block"
                >
                    <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-md shadow-[0_30px_80px_rgba(3,10,25,0.55)]">
                        <div className="flex items-center justify-between border-b border-white/10 pb-4">
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="h-5 w-5 text-sky-300" />
                                <span className="font-display text-sm font-semibold text-white">
                                    Integrated Systems
                                </span>
                            </div>
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-sky-300/80">
                                Live
                            </span>
                        </div>

                        <div className="mt-5 grid grid-cols-2 gap-3">
                            {[
                                ["Mobile", "Platforms & apps"],
                                ["Software", "Systems & web"],
                                ["Networks", "Hardware & LAN"],
                                ["Security", "CCTV & sites"],
                            ].map(([t, s]) => (
                                <div
                                    key={t}
                                    className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-transparent p-4"
                                >
                                    <div className="font-display text-lg font-semibold text-white">
                                        {t}
                                    </div>
                                    <div className="mt-0.5 text-xs text-slate-400">
                                        {s}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                            <div className="mb-2 flex items-center justify-between">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                    Connectivity
                                </span>
                                <span className="font-mono text-[10px] text-sky-300">
                                    Starlink · LAN · Wi-Fi
                                </span>
                            </div>
                            <div className="flex items-end gap-1.5">
                                {[40, 65, 52, 80, 60, 92, 74, 88].map((h, i) => (
                                    <motion.span
                                        key={i}
                                        initial={{ height: 6 }}
                                        animate={{ height: h }}
                                        transition={{
                                            duration: 1.1,
                                            delay: 0.8 + i * 0.06,
                                            ease: easeOut,
                                        }}
                                        className="w-full rounded-t bg-gradient-to-t from-brand to-sky-300"
                                        style={{ maxHeight: 96 }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
